import React from "react"
import cx from "classnames"
import { icons as iconTokens, theme } from "../../tokens"

import * as icons from "../../icons"
import { eqSet, capitalize } from "../utils"

if (
  !eqSet<string>(
    new Set(Object.keys(icons)),
    new Set(Object.keys(iconTokens.icons))
  )
)
  throw new Error(
    "Icon sources and tokens are not consistent. Did you add the source, export it and run 'pnpm run icons:update'"
  )

interface IconsMap {
  [name: string]: string | undefined
}

export const BUNDLED_ICONS: IconsMap = Object.freeze(
  Object.keys(iconTokens.icons).reduce(
    (bundledIcons, name) => ({
      ...bundledIcons,
      [name]: (icons as IconsMap)[name],
    }),
    {}
  )
)

export type IconSources = keyof typeof iconTokens.icons

const iconLegacyColorsOptions = [
  "amber",
  "blue",
  "coral",
  "graphite",
  "graphiteLight",
  "green",
  "greenDark",
  "greenLight",
  "grey",
  "greyDark",
  "greyLight",
  "greyLighter",
  "indigo",
  "indigoDark",
  "purple",
  "purpleLight",
  "red",
  "turquoise",
  "white",
] as const

export const iconColors = [
  ...(Object.keys(theme.icon) as Array<keyof typeof theme.icon>),
  ...iconLegacyColorsOptions,
]

export type IconColorsType = (typeof iconColors)[number]
export interface IconProps {
  source: IconSources
  /**
   * Available colors
   * @default base
   */
  color?: IconColorsType
  /**
   * Available size in px
   * @default 24
   */
  size?: 16 | 20 | 24 | 32
  contained?: boolean
  className?: string
}

export const isIconSource = (source: string): source is IconSources =>
  Object.keys(iconTokens.icons).includes(source)

export const Icon = ({
  source,
  color,
  size = 24,
  contained = false,
  className,
}: IconProps) => {
  const computedClassName = cx(
    className,
    `cobalt-Icon cobalt-Icon--${source}`,
    {
      [`cobalt-Icon--color${capitalize(color)}`]: color,
      "cobalt-Icon--size16": size === 16,
      "cobalt-Icon--size20": size === 20,
      "cobalt-Icon--size32": size === 32,
      "cobalt-Icon--contained": contained,
    }
  )

  const iconSource = BUNDLED_ICONS[source]

  if (iconSource == null)
    throw new Error(
      `Icon '${source}' can not be found. Did you add the source, export it and run 'pnpm run icons:update'?`
    )

  return (
    <span
      className={computedClassName}
      dangerouslySetInnerHTML={{ __html: iconSource }}
    />
  )
}

export * from "./__generated__/index"

export default Icon
